import React, { useState } from 'react';
import { useCollegePrepContext } from '../../contexts/CollegePrepContext';
import PersonalTimeline from './Timeline/PersonalTimeline';
import ActionTracker from './Timeline/ActionTracker';

const TimelinePlanning = () => {
  const { programs } = useCollegePrepContext();
  const [activeView, setActiveView] = useState('timeline'); // 'timeline', 'actions', 'overview'
  const timelinePeriods = [
    {
      id: 'early',
      title: 'Early Season',
      timeframe: 'Applications: January - February 2025',
      description: 'Most competitive programs with early deadlines',
      examples: ['Research Science Institute (RSI)', 'TASP', 'Governor\'s School programs', 'Highly competitive university programs'],
      color: 'bg-red-50 border-red-200 text-red-800',
      urgency: 'high'
    },
    {
      id: 'main',
      title: 'Main Season',
      timeframe: 'Applications: March - April 2025',
      description: 'Majority of summer programs open applications',
      examples: ['University research programs', 'Hospital internships', 'Community health programs', 'Regional programs'],
      color: 'bg-amber-50 border-amber-200 text-amber-800',
      urgency: 'medium'
    },
    {
      id: 'rolling',
      title: 'Rolling Applications',
      timeframe: 'Applications: April - June 2025',
      description: 'Programs with flexible deadlines and rolling admissions',
      examples: ['Local internships', 'Volunteer programs', 'Job opportunities', 'Independent projects'],
      color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      urgency: 'low'
    },
    {
      id: 'always',
      title: 'Always Available',
      timeframe: 'Apply Anytime',
      description: 'Opportunities you can pursue independently',
      examples: ['Online courses', 'Self-directed research', 'Part-time jobs', 'Volunteer work'],
      color: 'bg-azure-50 border-azure-200 text-azure-800',
      urgency: 'flexible'
    }
  ];

  const preparationSteps = [
    {
      month: 'September - October 2024',
      title: 'Research & Discovery',
      tasks: [
        'Define your interests and goals',
        'Research potential programs',
        'Start building your target list',
        'Connect with mentors and advisors'
      ],
      status: 'current'
    },
    {
      month: 'November - December 2024',
      title: 'Application Preparation',
      tasks: [
        'Request recommendation letters',
        'Draft personal statements',
        'Prepare for interviews',
        'Finalize program choices'
      ],
      status: 'upcoming'
    },
    {
      month: 'January - April 2025',
      title: 'Application Season',
      tasks: [
        'Submit early applications',
        'Complete main season applications',
        'Follow up with programs',
        'Apply for financial aid'
      ],
      status: 'future'
    },
    {
      month: 'May - June 2025',
      title: 'Decisions & Planning',
      tasks: [
        'Receive acceptances',
        'Make final decisions',
        'Complete enrollment',
        'Prepare for summer'
      ],
      status: 'future'
    }
  ];

  const getUrgencyIcon = (urgency) => {
    const icons = {
      'high': '🚨',
      'medium': '⚡',
      'low': '📅',
      'flexible': '🔄'
    };
    return icons[urgency] || '📅';
  };

  const getStatusColor = (status) => {
    const colors = {
      'current': 'bg-emerald-50 border-emerald-200',
      'upcoming': 'bg-amber-50 border-amber-200',
      'future': 'bg-stone-50 border-stone-200'
    };
    return colors[status] || 'bg-stone-50 border-stone-200';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Personal Planning Timeline</h2>
            <p className="text-stone-600">
              Your customized timeline based on your program collection and current phase.
            </p>
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('timeline')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeView === 'timeline'
                  ? 'bg-white text-azure-600 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setActiveView('actions')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeView === 'actions'
                  ? 'bg-white text-azure-600 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Actions
            </button>
            <button
              onClick={() => setActiveView('overview')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeView === 'overview'
                  ? 'bg-white text-azure-600 shadow-sm'
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              Overview
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Based on Active View */}
      {activeView === 'timeline' && (
        <PersonalTimeline />
      )}

      {activeView === 'actions' && (
        <ActionTracker programs={programs} />
      )}

      {activeView === 'overview' && (
        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📍</span>
              <h3 className="font-semibold text-emerald-800">Where You Are Now</h3>
            </div>
            <p className="text-sm text-emerald-700 mb-3">
              <strong>October 2024:</strong> Perfect timing to start your summer program research! Most competitive programs will open applications in 2-3 months.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                <span className="text-emerald-700">Research Phase (Active)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                <span className="text-amber-700">Application Prep (Next)</span>
              </div>
            </div>
          </div>

          {/* Application Seasons */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Application Seasons</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {timelinePeriods.map((period) => (
                <div key={period.id} className={`border rounded-lg p-4 ${period.color}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{getUrgencyIcon(period.urgency)}</span>
                    <h4 className="font-semibold">{period.title}</h4>
                  </div>
                  <p className="text-sm font-medium mb-2">{period.timeframe}</p>
                  <p className="text-sm mb-3">{period.description}</p>
                  <div className="text-xs">
                    <strong>Examples:</strong>
                    <ul className="mt-1 space-y-1">
                      {period.examples.map((example, index) => (
                        <li key={index} className="ml-2">• {example}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preparation Timeline */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Preparation Timeline</h3>
            <div className="space-y-4">
              {preparationSteps.map((step, index) => (
                <div key={index} className={`border rounded-lg p-4 ${getStatusColor(step.status)}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      step.status === 'current' ? 'bg-emerald-500 text-white' : 
                      step.status === 'upcoming' ? 'bg-amber-500 text-white' : 'bg-stone-300 text-stone-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900">{step.title}</h4>
                        {step.status === 'current' && (
                          <span className="px-2 py-1 text-xs font-medium bg-emerald-500 text-white rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-600 mb-3">{step.month}</p>
                      <ul className="space-y-1">
                        {step.tasks.map((task, taskIndex) => (
                          <li key={taskIndex} className="text-sm text-stone-700 flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">•</span>
                            <span>{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
            <h3 className="font-semibold text-sky-800 mb-3">Timeline Tips</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-sky-700">
              <div>
                <h4 className="font-medium mb-2">Early Action Strategy</h4>
                <ul className="space-y-1">
                  <li>• Apply to reach programs first (January deadlines)</li>
                  <li>• Have backup options ready</li>
                  <li>• Start essays early - they take time</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Stay Organized</h4>
                <ul className="space-y-1">
                  <li>• Use a calendar for all deadlines</li>
                  <li>• Set reminders 2 weeks before due dates</li>
                  <li>• Keep all documents in one place</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelinePlanning;